import { attachLegacyDocument, updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  FieldWrapper,
  Icons,
  Input,
  Label,
  LoadingIndicator,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

type UploadFileModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  file: FileBasic;
  setFile: Dispatch<SetStateAction<FileBasic>>;
  documentType: LegacyDocumentType;
  onUploadSuccess?: () => void;
  buttonText?: string;
  serviceRequest: ServiceRequestDetails;
};

const EINProofSchema = z.object({
  federalEIN: z
    .string()
    .regex(/^\d{2}-\d{7}$|^\d{9}$/, {
      message: 'Please enter a valid EIN in format XX-XXXXXXX or XXXXXXXXX',
    })
    .min(1, { message: 'Please enter a valid federal EIN number.' }),
});

type EINProofSchemaFields = z.infer<typeof EINProofSchema>;

export const UploadEINProofModal = ({
  isOpen,
  setIsOpen,
  file,
  setFile,
  documentType,
  onUploadSuccess,
  serviceRequest,
}: UploadFileModalProps) => {
  const {
    register,
    formState: { errors },
    getValues,
    watch,
    handleSubmit,
  } = useForm<EINProofSchemaFields>({
    resolver: zodResolver(EINProofSchema),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const documentResponse = await attachLegacyDocument(
      serviceRequest.corporationId,
      {
        ...file,
        documentType,
      }
    );
    const taxIdResponse = documentResponse.ok
      ? await updateBusinessEntity(serviceRequest.corporationId, {
          primaryTaxId: getValues('federalEIN'),
        })
      : undefined;
    if (!taxIdResponse?.ok) {
      setIsSubmitting(false);
      return;
    }
    if (onUploadSuccess) await onUploadSuccess();
    setIsSubmitting(false);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-6 flex flex-col gap-4">
        <div className="w-12 h-12 rounded-md bg-bg-success flex justify-center items-center">
          <Icons.Upload01 className="text-text-success" />
        </div>
        <div className="mt-4">
          <h3 className="text-lg-bold text-text-primary-900">Upload EIN</h3>
          <p className="text-sm-regular text-text-tertiary-600">
            This will be saved in your client&apos;s profile
          </p>
        </div>

        <FieldWrapper>
          <Label>
            Upload Document<span className="text-violet-solid ml-1">*</span>
          </Label>
          <SingleFileUploader
            companyId={serviceRequest.companyId}
            file={
              file
                ? {
                    fileName: file?.fileName,
                    fileUrl: file?.fileUrl,
                    documentType,
                  }
                : undefined
            }
            accept={{
              'application/pdf': ['.pdf'],
              'image/*': ['.png', '.jpg', '.jpeg'],
            }}
            setFile={setFile}
            token=""
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>
            Enter EIN<span className="text-violet-solid ml-1">*</span>
          </Label>
          <Input
            id="federalEIN"
            {...register('federalEIN')}
            error={errors.federalEIN?.message}
            defaultValue={watch('federalEIN')}
            placeholder="XX-XXXXXXX"
          />
        </FieldWrapper>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || !file?.fileUrl || !watch('federalEIN')}
            className="flex items-center gap-2"
          >
            {isSubmitting ? <LoadingIndicator /> : null} Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
